/**
 * Guides as shareable posts. Facts about named vendors are quoted only from their published
 * SLAs, checked on 2026-10-19; everything else is general and says so.
 */

export type Block =
  | { p: string }
  | { h: string }
  | { ul: string[] }
  | { ol: string[] }
  | { note: string }
  | { rows: [string, string, string][]; head: [string, string, string] };

export interface Post {
  slug: string;
  title: string;
  /** The line under the title, and the description when the link is shared. */
  dek: string;
  date: string;
  minutes: number;
  body: Block[];
}

export const POSTS: Post[] = [
  {
    slug: "read-an-sla-credit-clause",
    title: "How to read an SLA credit clause in five minutes",
    dek: "Three questions tell you whether a vendor owes you money: the promise, the credit, and the deadline.",
    date: "2026-10-23",
    minutes: 4,
    body: [
      {
        p: "Most SaaS and cloud contracts include a service level agreement, and most SLAs include a credit: a discount the vendor owes you when it misses what it promised. Many of those credits go unclaimed, not because the outages don't happen, but because nobody reads the clause closely enough to act on it.",
      },
      { p: "You don't need a lawyer to read one. You need three answers." },
      { h: "1. What's the promise?" },
      {
        p: "Usually a monthly uptime percentage for a named service, such as 99.9%. Some contracts add support response times by priority (a first reply within an hour for a critical issue) or a promise to notify you of incidents within a set time.",
      },
      {
        p: "Check which services the promise covers. A contract can promise 99.9% for the core product and nothing for an add-on you rely on just as much.",
      },
      { h: "2. What's the remedy?" },
      {
        p: "Look for a credit table: bands of uptime, each with a share of the monthly fee. Atlassian's published Cloud SLA is a clear example. For Premium customers:",
      },
      {
        head: ["Monthly uptime", "Credit", "Of"],
        rows: [
          ["Below 99.9%", "10%", "the monthly fee"],
          ["Below 99.0%", "25%", "the monthly fee"],
          ["Below 95.0%", "50%", "the monthly fee"],
        ],
      },
      {
        p: "Some contracts promise uptime and offer no credit at all. That's worth knowing too: the vendor's record of misses is still your best argument at renewal.",
      },
      { h: "3. What's the deadline, and how do you claim?" },
      {
        p: "This is where most credits are lost. Atlassian's published SLA asks for a support ticket within 15 days after the end of the month. AWS's EC2 SLA asks for a case in the AWS Support Center by the end of the second billing cycle after the incident. Miss the window and the credit is gone, however clear the outage was.",
      },
      {
        note: "Your negotiated contract may change all three answers. An enterprise order form often raises the promise, changes the credit table or extends the deadline. Read the version you signed.",
      },
      { h: "Doing it every month" },
      {
        p: "Reading one clause is easy. Reading every vendor's, measuring every month against it and filing before each deadline is the part nobody has time for. That's the work ninekeep does: it reads your contracts, checks each vendor monthly and files the claims on time, with the math shown.",
      },
    ],
  },
  {
    slug: "claim-deadlines",
    title: "SLA claim deadlines: why most credits are lost to the calendar",
    dek: "A credit is only owed if you ask in time and in the right way. The windows are short and start at different points.",
    date: "2026-10-23",
    minutes: 4,
    body: [
      {
        p: "An outage happens. Everyone remembers it. The month ends, the invoice arrives at the full price, and by the time anyone thinks of the credit, the window has closed. Claim deadlines are short, they start at different points in different contracts, and they almost never appear on anyone's calendar.",
      },
      { h: "Where the clock starts" },
      {
        head: ["Clock starts at", "Example", "Window"],
        rows: [
          ["The end of the month", "Atlassian Cloud (published SLA)", "15 days"],
          [
            "The billing cycle",
            "AWS EC2 (published SLA)",
            "Until the end of the second billing cycle after",
          ],
          [
            "The incident itself",
            "Contracts that require notice",
            "Often days, sometimes during the outage",
          ],
        ],
      },
      {
        p: "The third kind is the easiest to miss: some contracts only pay if you report the problem while it's happening, or within a few days. By the end of the month it's already too late.",
      },
      { h: "What a valid claim usually needs" },
      {
        ul: [
          "Your account or customer number",
          "The service and region affected",
          "Dates and times of the outage, ideally backed by your own records",
          "The address or portal the contract names, sometimes with exact wording in the subject",
        ],
      },
      {
        p: "AWS, for example, asks for specific wording in the subject line of the support case. Claims that miss a required detail can be refused even when the outage is undisputed.",
      },
      { h: "Keeping a calendar of credits" },
      {
        ol: [
          "List every vendor whose contract has a credit",
          "For each, note where the clock starts and how long the window is",
          "After every month, and after every incident, check which windows are open",
          "File the ones that are, with the proof attached",
        ],
      },
      {
        p: "It's simple, and it's easy to skip, because no one owns it. ninekeep keeps that calendar from your own contracts and files before each window closes.",
      },
    ],
  },
  {
    slug: "status-page-vs-your-contract",
    title: "Your vendor's status page isn't your contract",
    dek: "The vendor's uptime number isn't the one that counts. Your contract defines downtime its own way.",
    date: "2026-10-23",
    minutes: 3,
    body: [
      {
        p: "When there's an outage, the first place most teams look is the vendor's status page. It's a useful signal, but it's the vendor's own account of what happened, in the vendor's own words. Your credit depends on something else: how your contract defines downtime.",
      },
      { h: "Where the two disagree" },
      {
        ul: [
          "Partial outages marked 'degraded performance' that still count as downtime under the contract's definition",
          "Regional incidents that hit only the region you use, while the global status stays green",
          "Maintenance that runs past the window the contract allows",
          "Several short incidents in one month that add up to a breach no single one reaches",
        ],
      },
      {
        p: "Definitions vary. Atlassian's published SLA, for instance, counts a minute as downtime when the error rate in that minute is above 5%, and says Atlassian's own monitoring is the measure. Other contracts measure by region, by request, or by the services you actually bought.",
      },
      { h: "What to keep" },
      {
        p: "Whatever the definition, the claim is stronger with your own record: when you saw the problem, what failed, and for how long. Incident emails from the vendor count too, so keep them.",
      },
      {
        p: "ninekeep matches status-page incidents to the services in your contract, adds your own outside checks and the vendor's own emails, and measures each month against your contract's definition rather than the vendor's headline number.",
      },
    ],
  },
  {
    slug: "claim-letter-checklist",
    title: "The SLA credit claim checklist",
    dek: "What to put in a credit claim so it's paid the first time.",
    date: "2026-10-23",
    minutes: 3,
    body: [
      {
        p: "Vendors refuse claims that are late, vague or sent to the wrong place. A good claim is short, specific and quotes the contract back to the vendor. Use this before you send one.",
      },
      { h: "Include" },
      {
        ul: [
          "Your account reference and the contract or order form the service falls under",
          "The month, the service and the uptime you measured",
          "The clause, quoted, and the credit band your month falls in",
          "The credit amount, with the calculation shown step by step",
          "Incident dates and times, with your proof attached",
          "Any wording the contract requires, such as a set subject line",
        ],
      },
      { h: "Avoid" },
      {
        ul: [
          "Claiming for services the contract doesn't cover",
          "Rounding in your favour: use the contract's own numbers",
          "Sending to a general inbox when the contract names a portal or address",
          "Waiting for the next invoice before you file",
        ],
      },
      { h: "A worked example" },
      {
        p: "A service costs $12,000 a year, so $1,000 a month. The contract promises 99.9% uptime and pays 10% of the month's fee below that. In August the service was down for an hour, which measured 99.87% for the month. The claim: 10% of $1,000, so $100, filed with the incident times attached, a month before the deadline.",
      },
      {
        p: "ninekeep drafts each claim this way from your contract, with the proof attached, and follows it until the credit lands.",
      },
    ],
  },
  {
    slug: "two-real-slas",
    title: "Two real SLAs, side by side: AWS EC2 and Atlassian Cloud",
    dek: "Same idea, different rules. What two published SLAs promise, pay and require.",
    date: "2026-10-23",
    minutes: 3,
    body: [
      {
        p: "Every SLA has the same three parts: a promise, a credit and a way to claim it. The details differ enough that a habit learned on one vendor will lose you money on another. Here are two published SLAs, as their vendors publish them.",
      },
      {
        head: ["", "AWS EC2", "Atlassian Cloud (Premium)"],
        rows: [
          ["Promise", "99.99% a month per region, across two or more zones", "99.9% a month"],
          ["Credit bands", "10% · 30% · 100%", "10% · 25% · 50%"],
          ["Deadline", "End of the second billing cycle after", "15 days after the month ends"],
          ["How", "A case in the AWS Support Center", "A ticket at support.atlassian.com"],
          ["Paid as", "Credit on future EC2 bills, over $1", "Credit on a future payment"],
        ],
      },
      { h: "What the difference means" },
      {
        ul: [
          "AWS gives you longer to claim; Atlassian gives you two weeks",
          "AWS's promise applies only if you run across two or more Availability Zones; a single instance has its own, lower 99.5% promise",
          "Both pay credits, not cash, so a claim you skip is money you simply pay",
        ],
      },
      {
        note: "Checked against each vendor's published SLA on October 19, 2026. Negotiated contracts often differ; use the one you signed.",
      },
    ],
  },
  {
    slug: "glossary",
    title: "SLA terms in plain words",
    dek: "The words vendors use in service level agreements, and what they mean for you.",
    date: "2026-10-23",
    minutes: 2,
    body: [
      { h: "Service credit" },
      {
        p: "A discount on a future invoice that the vendor owes when it misses its SLA. Rarely paid as cash.",
      },
      { h: "Monthly uptime percentage" },
      {
        p: "The share of the month the service was available, by the contract's own definition of downtime.",
      },
      { h: "Credit table" },
      {
        p: "The bands that set the credit: for example, below 99.9% earns 10% of the monthly fee.",
      },
      { h: "Claim window" },
      { p: "How long you have to ask for a credit, and the point the clock starts from." },
      { h: "Exclusions" },
      {
        p: "Outages the SLA doesn't count, such as scheduled maintenance or problems on your side.",
      },
      { h: "Sole remedy" },
      {
        p: "A clause saying credits are all you can get for missed uptime. It makes claiming them more important, not less.",
      },
    ],
  },
];

export const postPath = (p: Post) => `/resources/${p.slug}`;
